import type { ComposeFormValue } from "@/contexts/composer-context";
import type { Status } from "@/schemas";

type BoostableCheck =
  | { boostable: true }
  | { boostable: false; reason: BoostableBlockReason };

type BoostableBlockReason =
  | "is_reply"
  | "is_quote"
  | "is_group_post"
  | "is_self_visibility"
  | "is_scheduled"
  | "is_prediction";

type ComposerSnapshot = Pick<
  ComposeFormValue,
  "inReplyToStatusId" | "groupId" | "scheduledAt" | "isPredictions"
> & {
  // Quote is derived from the composer `embed` (embed.statusId), not a form
  // field, so it isn't picked from ComposeFormValue. See compose-form.tsx.
  quotedStatusId?: string;
  visibility?: string;
};

/**
 * Boost gating shape — both ComposerSnapshot and a published Status project
 * into this. Keeping the gating logic in one place ensures the composer
 * (Flow A) and status menu (Flow B) accept the same set of posts.
 */
type BoostableShape = {
  isReply: boolean;
  isQuote: boolean;
  isGroupPost: boolean;
  isScheduled: boolean;
  isPrediction: boolean;
  visibility?: string;
};

/**
 * Naming convention: `isBoostable*` returns a `BoostableCheck` verdict
 * (boostable + reason on failure), NOT a TypeScript `value is T` predicate.
 * Callers inspect `.boostable`; they cannot narrow on the call. The `is`
 * prefix mirrors English ("is this boostable?"), but the value is a tagged
 * result so we can surface *why* a post is rejected for analytics / UI.
 * See review 014.
 */
function isBoostableShape(shape: BoostableShape): BoostableCheck {
  if (shape.isReply) return { boostable: false, reason: "is_reply" };
  if (shape.isQuote) return { boostable: false, reason: "is_quote" };
  if (shape.isGroupPost) return { boostable: false, reason: "is_group_post" };
  if (shape.isScheduled) return { boostable: false, reason: "is_scheduled" };
  if (shape.isPrediction) return { boostable: false, reason: "is_prediction" };
  if (shape.visibility && shape.visibility !== "public") {
    return { boostable: false, reason: "is_self_visibility" };
  }
  return { boostable: true };
}

/**
 * Client-side eligibility check for whether the current composer state can
 * produce a boostable post. Hides the toggle entirely when the answer is no;
 * does not attempt to second-guess backend ineligibility reasons (those are
 * surfaced via the /eligibility endpoint).
 */
function isBoostableComposerState(snapshot: ComposerSnapshot): BoostableCheck {
  // Use `!= null` rather than `Boolean(...)` so a future schema migration
  // that changes a sentinel ("0", "") into a real value doesn't silently
  // flip the gate. See review 014.
  return isBoostableShape({
    isReply: snapshot.inReplyToStatusId != null && snapshot.inReplyToStatusId !== "",
    isQuote: snapshot.quotedStatusId != null && snapshot.quotedStatusId !== "",
    isGroupPost: snapshot.groupId != null && snapshot.groupId !== "",
    isScheduled: snapshot.scheduledAt != null,
    isPrediction: snapshot.isPredictions === true,
    visibility: snapshot.visibility,
  });
}

/**
 * Client-side eligibility check for an already-published Status. Used by the
 * status menu to decide whether to surface the "Boost Truth" item. Mirrors
 * `isBoostableComposerState` so a post that the composer would have refused
 * to allow boosting at create time is also refused boost-after-the-fact.
 *
 * Published statuses don't carry a `scheduled_at` field — the schema is
 * post-publication — so `isScheduled` is always false here.
 */
function isBoostableStatus(
  status: Pick<
    Status,
    "in_reply_to_id" | "quote" | "group" | "prediction" | "visibility"
  >,
): BoostableCheck {
  return isBoostableShape({
    isReply: status.in_reply_to_id != null && status.in_reply_to_id !== "",
    isQuote: status.quote != null,
    isGroupPost: status.group != null,
    isScheduled: false,
    isPrediction: status.prediction != null,
    visibility: status.visibility,
  });
}

export {
  isBoostableComposerState,
  isBoostableStatus,
  type BoostableCheck,
  type BoostableBlockReason,
  type ComposerSnapshot,
};
